import { Chip, Switch, Text, Textarea, TextInput, Space } from '@mantine/core';
import { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from '@mantine/form';
import { notifications } from "@mantine/notifications";
import { DateTime } from "luxon";
import { Sheet } from "react-modal-sheet";
import { useListState } from '@mantine/hooks';
import { createComment } from "../../lib/comments";
import { GuideContext, HomeContext, TripContext, UserContext } from '../../lib/context';
import { useFirebaseRefs } from "../../lib/firebase";
import { Guide, Place, PlaceClassType, PlaceType, SavedType, Trip } from '../../lib/interfaces';
import { SearchChipsAndList } from "../SearchUI/SearchUI";
import { Position, positionFromLatLng } from '../../lib/position';
import {
    geocodeAndUpdatePlace, insertGuidePlace,
    insertSavedPlace,
    insertTripActivity,
    isLodging,
    placeTypeFromPlace,
    placeTypeLabel,
    updateGuidePlace,
    updateSavedPlace,
    updateTripActivity,
} from '../../lib/placeFunctions';
import { raceOrNotify } from "../../lib/promises";
import { SheetsData } from "../Sheets";
import { newSearchQuery } from "../../lib/search";
import { ModalChoiceOptions, showModalAsync } from "../TSModal";
import { blurOnEnterKey } from "../../lib/eventHandlers";
import { isIdea, realDate, TSDate } from '../../lib/time';
import { StepWizard } from '../StepWizard/StepWizard';
import { ClearTextAreaRightSection } from "../AddEditTrip/ClearTextAreaRightSection";
import { useTripById } from "../../lib/hooks";
import { computeBias } from "../../lib/geolocation";
import { TSDatePicker } from "../TSDatePicker";

import stepWizardClasses from '../StepWizard/StepWizard.module.css';
import placeClasses from "../SavedPlacesList/SavedPlacesList.module.css";
import { computeDistance, Distance, distanceTextForDistance } from '../../lib/distance';
import { TSScrollable } from '../TSScrollable';
import { useKeyboardScrollEffect } from '../../lib/keyboard';

/**
 * Shows a series of modal sheets collecting information to add a trip activity or saved place.
 *
 * This is akin to {@link showAddEditTripSavedPlace}, but always runs in add-mode; this is particularly
 * suitable when adding a saved place to a trip.
 */
export async function showAddPlace(sheets: SheetsData, placeTemplate: Partial<Place>, currentTrip: Trip | undefined) {
    const copy: Partial<Place> = {
        placeid: placeTemplate.placeid,
        placetype: placeTemplate.placetype,
        activityType: placeTemplate.activityType,
        title: placeTemplate.title,
        location: placeTemplate.location,
        address: placeTemplate.address,
        lat: placeTemplate.lat,
        lng: placeTemplate.lng,
        locality: placeTemplate.locality,
    };

    await showAddEditTripSavedPlace(sheets, copy, currentTrip);
}

function addCategory(placeTemplate: Partial<Place>, category: SavedType) {
    placeTemplate.categories = [
        category,
        ...(placeTemplate.categories ?? []).filter(c => c !== category),
    ];
}

function removeCategory(placeTemplate: Partial<Place>, category: SavedType) {
    placeTemplate.categories = (placeTemplate.categories ?? []).filter(c => c !== category);
}

/**
 * Shows a series of modal sheets collecting information to create or edit a trip activity
 * or saved place.
 *
 * If the `placeTemplate` parameter contains a `placeclass` field, the add / edit experience
 * is configured for that type. Else, if `placeTemplate.tripdocid` is specified, we assume
 * that this is an add / edit of a trip activity.
 *
 * Otherwise, a modal sheet is presented to the user to gather the appropriate place type.
 *
 * Once the place type has been determined, the user is presented with a sheet with a little
 * configurator wizard.
 */
export async function showAddEditTripSavedPlace(sheets: SheetsData, placeTemplate: Partial<Place>, currentTrip: Trip | undefined) {
    let placeClassType: PlaceClassType;
    if (placeTemplate.placeclass) {
        placeClassType = placeTemplate.placeclass;
        if ((placeTemplate.categories ?? []).length === 0) {
            const choices: ModalChoiceOptions[] = [
                { title: "Favorite", type: "normal" },
                { title: "Want to go", type: "normal" },
            ];
            const response = await showModalAsync(sheets, "Saved Place Type", choices);
            if (response.type === 'dismiss') {
                return;
            } else if (response.choice.title === 'Favorite') {
                addCategory(placeTemplate, 'Favorite');
            } else if (response.choice.title === 'Want to go') {
                addCategory(placeTemplate, 'Want to go');
            } else {
                console.error(`Disregarding unknown category response choice: ${response.choice.title}`);
                return;
            }
        }
    } else if (placeTemplate.tripdocid) {
        placeClassType = PlaceClassType.Activity;
    } else {
        const choices: ModalChoiceOptions[] = [
            { title: "Favorite", type: "normal" },
            { title: "Want to go", type: "normal" },
            { title: "Trip Activity", type: "normal" },
        ];
        const response = await showModalAsync(sheets, "Add to Travel Scroll", choices);
        if (response.type === 'dismiss') {
            return;
        } else if (response.choice.title === 'Favorite') {
            placeClassType = PlaceClassType.Saved;
            addCategory(placeTemplate, 'Favorite');
        } else if (response.choice.title === 'Want to go') {
            placeClassType = PlaceClassType.Saved;
            addCategory(placeTemplate, 'Want to go');
        } else if (response.choice.title === 'Trip Activity') {
            placeClassType = PlaceClassType.Activity;
            if (currentTrip) {
                placeTemplate = { ...placeTemplate, tripdocid: currentTrip!.docid };
            } else {
                const selectedTrip = await showGatherTripSheet(sheets);
                if (selectedTrip) {
                    placeTemplate = { ...placeTemplate, tripdocid: selectedTrip.docid };
                } else {
                    return;
                }
            }
        } else {
            console.error(`Disregarding unknown response choice: ${response.choice.title}`);
            return;
        }
    }

    placeTemplate.placeclass = placeClassType;
    const title = (() => {
        const categoryText = placeTemplate.categories?.length === 1 ? placeTemplate.categories[0] : 'Saved Place';
        if (placeTemplate.docid) {
            if (placeClassType === PlaceClassType.Activity) {
                return `Edit ${placeTemplate.title ?? 'Trip Activity'}`;
            } else if (placeClassType === PlaceClassType.Saved) {
                return `Edit ${categoryText}`;
            } else {
                return 'Edit';
            }
        } else if (placeClassType === PlaceClassType.Activity) {
                return `Add ${placeTemplate.title ?? 'New Activity'}`;
            } else if (placeClassType === PlaceClassType.Saved) {
                return `Add New ${categoryText}`;
            } else {
                return 'Add';
            }
    })();

    await new Promise<void>(resolve => {
        sheets.showSheetWith(
            context => <AddEditPlace
                onClose={() => {
                    context.removeSheet();
                    resolve();
                }}
                place={placeTemplate}
                sheetState={context.sheetState}
            />,
            {
                title,
                disableDrag: true, // TODO enable dragging, and figure out why the scroll area fails to drag properly in this context
            }
        );
    });
}

export async function showAddEditGuidePlace(
    sheets: SheetsData,
    placeTemplate: Partial<Place>,
    guide: Guide
): Promise<Partial<Place> | undefined> {
    placeTemplate.placeclass = PlaceClassType.Guide;
    placeTemplate.guideId = guide.docid;

    return new Promise<Partial<Place> | undefined>((resolve) => {
        sheets.showSheetWith(
            (context) => (
                <AddEditPlace
                    onClose={() => {
                        context.removeSheet();
                        resolve(undefined);
                    }}
                    place={placeTemplate}
                    sheetState={context.sheetState}
                    onPlaceCreated={(createdPlace) => {
                        context.removeSheet();
                        resolve(createdPlace);
                    }}
                />
            ),
            {
                title: 'Add Guide Place',
                disableDrag: true,
            }
        );
    });
}

export async function showGatherTripSheet(sheets: SheetsData) {
    return new Promise<Trip | undefined>(resolve => {
        sheets.showSheetWith(
            context => <Sheet.Scroller draggableAt='top'>
                <GatherTrip onTripSelected={trip => {
                    context.removeSheet();
                    resolve(trip);
                }} />
            </Sheet.Scroller>,
            {
                detent: "content-height",
                disableDrag: false,
                title: "Choose a trip",
                onSheetClosed: () => resolve(undefined),
            });
    });
}

export function GatherTrip(props: { onTripSelected: (trip: Trip | undefined) => unknown }) {
    const { userTrips } = useContext(HomeContext);

    return <div
        style={{ display: 'flex', flexDirection: 'column', gap: "0.5rem", height: '20rem' }}
        className='safeAreaPaddingBottom'
        >
        { userTrips?.map((trip, index) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div
                key={index}
                className={placeClasses.item}
                onClick={() => props.onTripSelected(trip)}
            >
                <Text>{trip.title}</Text>
                { realDate(trip.startdate) && <Text size='sm' color='dimmed'>
                    {realDate(trip.startdate)!.toLocalDateTime().toLocaleString(DateTime.DATE_HUGE)}
                </Text> }
            </div>
        )) }
    </div>;
}

export async function showGatherGuideSavedPlaceSheet(sheets: SheetsData, guide: Guide) {
    return new Promise<Place | undefined>(resolve => {
        sheets.showSheetWith(
            context => <Sheet.Scroller draggableAt='top'>
                <GatherSavedPlace
                    guide={guide}
                    onSavedPlaceSelected={place => {
                        context.removeSheet();
                        resolve(place);
                }} />
            </Sheet.Scroller>,
            {
                detent: "content-height",
                disableDrag: false,
                title: "Add a Saved Place",
                onSheetClosed: () => resolve(undefined),
            });
    });
}

type GatherSavedPlaceProps = {
    guide: Guide;
    onSavedPlaceSelected: (place: Place | undefined) => unknown;
};

export function GatherSavedPlace({ guide, onSavedPlaceSelected }: GatherSavedPlaceProps) {
    const { uidPlaces } = useContext(HomeContext);
    type PotentiallyLocatedPlace = { place: Place, distance: Distance };
    const [ placesToShow, placesToShowForGuide ] = useListState<PotentiallyLocatedPlace>([]);

    useEffect(() => {
        const withDistance = uidPlaces.map(place => ({ place, distance: computeDistance(place, null, guide, null, null) }));
        // Places without distance.fromGuide go to the end
        withDistance.sort(
            (a, b) =>
                (a.distance?.fromGuide ?? Infinity) - (b.distance?.fromGuide ?? Infinity)
        );
        placesToShowForGuide.setState(withDistance);
    }, [ uidPlaces, guide ]);

    return <div
        style={{ display: 'flex', flexDirection: 'column', gap: "0.5rem", height: '40rem' }}
        className='safeAreaPaddingBottom'
    >
        {placesToShow?.map((item, index) => {
            const distanceText = distanceTextForDistance(
                null,
                guide,
                false,
                PlaceClassType.Saved,
                item.distance,
                null
            );
            return (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                <div
                    key={item.place.docid}
                    className={placeClasses.item}
                    style={{ paddingTop: '0rem',
                        paddingBottom: '0rem' }}
                    onClick={() => onSavedPlaceSelected(item.place)}
                >
                    <Text>{item.place.title ?? item.place.location}</Text>
                    {item.place.address && (
                        <Text size="sm" color="dimmed">
                            {item.place.address}
                        </Text>
                    )}
                    {item.distance.fromGuide !== undefined && (
                        <Text
                            lineClamp={1}
                            style={{ textOverflow: "ellipsis", overflow: "hidden" }}
                            color="dimmed"
                            size="xs"
                        >
                            {distanceText}
                        </Text>
                    )}
                </div>
            );
        })}
    </div>;
}

export function AddEditPlace({
    place,
    onClose,
    sheetState,
    onPlaceCreated,
}: {
    place: Partial<Place>;
    onClose: () => unknown;
    sheetState: 'closed' | 'opening' | 'opened';
    onPlaceCreated?: (place: Partial<Place>) => void;
}) {
    const firebaseRefs = useFirebaseRefs();
    const { firebaseUserId: currentUserId } = useContext(UserContext);
    const user = useContext(UserContext);
    const commentTitleTextAreaRef = useRef<HTMLParagraphElement>(
        null as unknown as HTMLParagraphElement
    );
    const addressTitleTextAreaRef = useRef<HTMLParagraphElement>(
        null as unknown as HTMLParagraphElement
    );

    const [ currentStep, setCurrentStep ] = useState(0);
    const [ position, setPosition ] = useState<Position | undefined>(positionFromLatLng(place));
    const [ locality, setLocality ] = useState(place.locality);
    const [ activityType, setActivityType ] = useState<PlaceType | undefined>(placeTypeFromPlace(place));
    const [ googlePlaceId, setGooglePlaceId ] = useState<string | undefined>(place.placeid ?? undefined);
    const [ categories, setCategories ] = useState(place.categories ?? []);
    const [ draft, setDraft ] = useState(place.isdraft ?? false);
    // Transit destination (second endpoint). Only persisted when the type is Transit.
    const [ endPosition, setEndPosition ] = useState<Position | undefined>(
        positionFromLatLng({ lat: place.endlat, lng: place.endlng }),
    );
    const [ endLocation, setEndLocation ] = useState<string | undefined>(place.endlocation ?? undefined);
    const [ endAddress, setEndAddress ] = useState<string | undefined>(place.endaddress ?? undefined);
    // The destination search box text, kept separate from the chosen endLocation so results clear
    // once a destination is picked.
    const [ endQuery, setEndQuery ] = useState('');
    const trip = useTripById(place?.tripdocid ?? null);
    const { currentTrip } = useContext(TripContext);
    const { currentGuide } = useContext(GuideContext);

    const mainFocusRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    useKeyboardScrollEffect(commentTitleTextAreaRef);
    useKeyboardScrollEffect(addressTitleTextAreaRef);

    const form = useForm({
        initialValues: {
            title: place.title,
            dateRange: [
                isIdea(place.startdatetime) ? null : place.startdatetime ?? null,
                isIdea(place.enddatetime) ? null : place.enddatetime ?? null,
            ] as [TSDate | null, TSDate | null],
            address: place.address ?? '',
            location: place.location ?? '',
            comment: '',
        },

        validate: (values) => {
            if (currentStep >= 1) {
                return {
                    title:
                        !values.title || values.title.trim().length < 1
                            ? 'A title is required'
                            : null,
                };
            } else {
                return {};
            }
        },
    });

    useEffect(() => {
        if (sheetState === 'opened' && mainFocusRef.current) {
            mainFocusRef.current.select();
        }
    }, [sheetState, mainFocusRef.current]);

    useEffect(() => {
        if (place.title && place.location && place.address) {
            setCurrentStep(1);
        }
    }, [place.title, place.location, place.address]);

    useEffect(() => {
        if (currentStep === 1) {
            // When entering the second (zero-based) step, copy the location field to the title if
            // we haven't assigned a title yet.
            if (form.values.title === undefined || form.values.title === null) {
                // only set the 'title' field value if we haven't done so already, either
                // via out-of-order components or via pre-existing values (e.g., an edit)
                form.setFieldValue('title', form.values.location);
            }
        }
    }, [currentStep, form.values.title, form.values.location]);

    const upsertActivity = async () => {
        const tripDocId = place.tripdocid;
        const placeDocId = place.docid;

        if (!tripDocId) {
            notifications.show({ message: `Failed to save your activity!` });
            console.warn('Refusing to update a record with no trip id!');
            return;
        }

        const partialActivity = await buildPlaceRecordAndResetForm();

        if (placeDocId) {
            await updateTripActivity(firebaseRefs, tripDocId, placeDocId, partialActivity);
        } else {
            await insertTripActivity(
                firebaseRefs,
                tripDocId,
                user,
                partialActivity,
                async (insertedDocId) => {
                    if (form.values.comment) {
                        await createComment(
                            firebaseRefs,
                            user,
                            { docid: insertedDocId, tripdocid: tripDocId },
                            form.values.comment
                        );
                    }
                }
            );
        }
    };

    const upsertSavedPlace = async () => {
        const placeDocId = place.docid;
        const partialPlace = await buildPlaceRecordAndResetForm();

        if (placeDocId) {
            await updateSavedPlace(firebaseRefs, user, placeDocId, partialPlace);
        } else {
            await insertSavedPlace(firebaseRefs, user, partialPlace);
        }
    };

    const upsertGuidePlace = async (): Promise<string | undefined> => {
        const { guideId } = place;
        const placeDocId = place.docid;

        if (!guideId) {
            notifications.show({ message: `Failed to save your place!` });
            console.warn('Refusing to update a record with no guide id!');
            return undefined;
        }

        const partialPlace = await buildPlaceRecordAndResetForm();

        if (placeDocId) {
            await updateGuidePlace(firebaseRefs, user, guideId, placeDocId, partialPlace);
            return placeDocId;
        } else {
            return insertGuidePlace(firebaseRefs, user, guideId, partialPlace);
        }
    };

    const upsertRecord = async (): Promise<string | undefined> => {
        switch (place.placeclass) {
            case PlaceClassType.Activity:
                await upsertActivity();
                return undefined;
            case PlaceClassType.Saved:
                await upsertSavedPlace();
                return undefined;
            case PlaceClassType.Guide:
                return upsertGuidePlace();
            default:
                notifications.show({
                    message: `Failed to save changes! Place class: ${place.placeclass}`,
                });
                return undefined;
        }
    };

    async function buildPlaceRecordAndResetForm() {
        const partialActivity: Partial<Place> = {
            location: form.values.location,
            address: form.values.address,
            title: form.values.title ?? '',
            activityType,
            placetype: activityType,
            categories,
            locality,
            startdatetime: form.values.dateRange[0],
            enddatetime: form.values.dateRange[1],
            ...(place.placeclass === PlaceClassType.Activity ? { isdraft: draft || null } : {}),
        };

        if (position) {
            partialActivity.lat = position.lat;
            partialActivity.lng = position.lng;
        } else {
            partialActivity.lat = null;
            partialActivity.lng = null;
        }

        // Persist the destination endpoint only for Transit; clear it otherwise (e.g. if the type was
        // changed away from Transit) so stale endpoints don't linger.
        if (activityType === PlaceType.Transit && endPosition) {
            partialActivity.endlat = endPosition.lat;
            partialActivity.endlng = endPosition.lng;
            partialActivity.endlocation = endLocation ?? null;
            partialActivity.endaddress = endAddress ?? null;
        } else {
            partialActivity.endlat = null;
            partialActivity.endlng = null;
            partialActivity.endlocation = null;
            partialActivity.endaddress = null;
        }

        partialActivity.placeid = googlePlaceId ?? place.placeid;

        if (locality) {
            partialActivity.locality = locality;
        } else {
            partialActivity.locality = null;
        }

        // Now that we've extracted all the data, we reset the form. We do this here so that it happens
        // before any of the await logic in the various upsert functions, so that we don't end up clearing
        // after a delay.
        form.reset();

        await geocodeAndUpdatePlace(place, partialActivity);
        return partialActivity;
    }

    const steps = ['Location', 'Details', 'Address'];
    const nextStep = () =>
        setCurrentStep((current) => {
            if (form.validate().hasErrors) {
                return current;
            }
            return current < steps.length ? Math.min(current + 1, steps.length - 1) : current;
        });

    const manageCategories = (checked: boolean, category: SavedType) => {
        const template = { categories };
        if (checked) {
            addCategory(template, category);
        } else {
            removeCategory(template, category);
            if (template.categories?.length === 0) {
                addCategory(template, category === 'Favorite' ? 'Want to go' : 'Favorite');
            }
        }
        setCategories(template.categories);
    };

    return (
        <StepWizard
            steps={steps}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            nextStep={nextStep}
            onDone={async () => {
                const { title } = form.values;

                // Build the place record to pass back
                const builtPlace: Partial<Place> = {
                    ...place,
                    location: form.values.location,
                    address: form.values.address,
                    title: form.values.title ?? '',
                    placetype: activityType,
                    locality,
                };
                if (position) {
                    builtPlace.lat = position.lat;
                    builtPlace.lng = position.lng;
                }
                builtPlace.placeid = googlePlaceId ?? place.placeid;

                // For guide places, we need the docid back before inserting the link
                if (place.placeclass === PlaceClassType.Guide) {
                    const isNewPlace = !place.docid;
                    try {
                        const newDocId = await upsertGuidePlace();
                        if (newDocId) {
                            builtPlace.docid = newDocId;
                        }
                        const verb = isNewPlace ? 'Created' : 'Updated';
                        const msg = title ? `${verb} ${title}` : verb;
                        notifications.show({ message: msg, autoClose: true });
                    } catch (e) {
                        const verb = isNewPlace ? 'create' : 'update';
                        const msg = title
                            ? `Failed to ${verb} ${title}!`
                            : `Failed to ${verb}!`;
                        notifications.show({ message: msg, color: 'red', autoClose: true });
                        console.error(e);
                    }
                } else {
                    const isNewPlace = !place.docid;
                    const verb = isNewPlace ? 'Created' : 'Updated';
                    const failVerb = isNewPlace ? 'create' : 'update';
                    raceOrNotify(
                        upsertRecord(),
                        500,
                        `${verb}${title ? ` ${title}` : ''}`,
                        `Failed to ${failVerb}${title ? ` ${title}` : ''}!`
                    );
                }

                onPlaceCreated?.(builtPlace);
                onClose();
            }}
        >
            {currentStep === 0 && (
                <div
                    className="fade-in"
                    style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
                >
                    <hr style={{ width: '100%' }} />
                    <TextInput
                        ref={mainFocusRef}
                        autoFocus={sheetState === 'opened'}
                        {...form.getInputProps('location')}
                        placeholder="Optional"
                        onKeyUp={blurOnEnterKey}
                        rightSection={
                            <ClearTextAreaRightSection
                                onClear={() => {
                                    form.setFieldValue('location', '');
                                    mainFocusRef.current?.select();
                                }}
                            />
                        }
                    />
                    <SearchChipsAndList
                        searchQuery={newSearchQuery(
                            {
                                searchTerm: (form.values as any).location,
                                chipConfig: {
                                    tripGuideChips: true,
                                    recents: true,
                                    savedPlaces: true,
                                    searchEngine: true,
                                },
                                emptyQueryBehavior: 'match-none',
                                bias: computeBias([
                                    positionFromLatLng(trip),
                                    positionFromLatLng(currentTrip),
                                ]),
                            },
                            () => {}
                        )}
                        places={[]}
                        userTrips={[]}
                        currentUserId={currentUserId ?? undefined}
                        uiState="inline"
                        detailMode="results"
                        notifySearchHits={() => {}}
                        onClick={async (result) => {
                            if (result.type === 'place') {
                                if (result.place.location) {
                                    form.setFieldValue('location', result.place.location);
                                }
                                if (result.place.address) {
                                    form.setFieldValue('address', result.place.address);
                                }
                                setPosition(positionFromLatLng(result.place));
                                setLocality(result.place.locality ?? undefined);
                                setActivityType(result.place.placetype ?? undefined);
                                setGooglePlaceId(result.place.placeid ?? undefined);
                            } else {
                                if (result.title) {
                                    form.setFieldValue('location', result.title);
                                }
                                setLocality(await result.locality());
                                setPosition(await result.position());
                                setActivityType(result.placeType);
                                form.setFieldValue('address', (await result.full_address()) ?? '');
                                if (result.type === 'google-search') {
                                    setGooglePlaceId(result.value.place_id);
                                }
                            }
                            nextStep();
                        }}
                    />
                </div>
            )}

            {currentStep === 1 && (
                <div className={`${stepWizardClasses.stepSection} fade-in`}>
                    <hr style={{ width: '100%' }} />
                    <div
                        className={stepWizardClasses.progress}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        <div>Title: </div>
                        <TextInput
                            ref={mainFocusRef}
                            autoFocus={sheetState === 'opened'}
                            style={{ display: 'inline-block', flex: 1, paddingLeft: '0.5rem' }}
                            {...form.getInputProps('title')}
                            value={form.values.title ?? ''}
                            onInput={(e) => form.setFieldValue('title', e.currentTarget.value)}
                            enterKeyHint="done"
                            onKeyUp={blurOnEnterKey}
                            rightSection={
                                <ClearTextAreaRightSection
                                    onClear={() => {
                                        form.setFieldValue('title', '');
                                        mainFocusRef.current?.select();
                                    }}
                                />
                            }
                        />
                    </div>
                    <div className={stepWizardClasses.progress}>
                        Location:{' '}
                        <span className={stepWizardClasses.fieldValue}>{form.values.location}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span className={stepWizardClasses.progress}>Choose a type:</span>
                        <div style={{ display: 'flex', flexWrap: 'nowrap', overflowY: 'auto' }}>
                            <Chip.Group
                                multiple={false}
                                value={activityType}
                                onChange={(value) => {
                                    if (isLodging({ activityType: value as PlaceType })) {
                                        form.setValues({
                                            dateRange: [
                                                place.startdatetime ?? null,
                                                place.startdatetime?.plus({ days: 1 }) ?? null,
                                            ],
                                        });
                                    }
                                    // Chip values are the PlaceType values themselves (e.g. Transit's
                                    // value is 'Flight'), so no member-name lookup is needed.
                                    setActivityType(value as PlaceType);
                                }}
                            >
                                <Chip value={PlaceType.Meal} size="xs">
                                    Meal
                                </Chip>
                                <Chip value={PlaceType.Lodging} size="xs">
                                    Lodging
                                </Chip>
                                <Chip value={PlaceType.Transit} size="xs">
                                    Transit
                                </Chip>
                                <Chip value={PlaceType.Activity} size="xs">
                                    Activity
                                </Chip>
                            </Chip.Group>
                        </div>
                    </div>
                    {place.placeclass === PlaceClassType.Activity && (
                        // Wrapper keeps the date picker from flex-growing into the column (its
                        // DatePickerInput sets flex:2), which left a big gap above the destination.
                        <div>
                            <TSDatePicker
                                dates={form.values.dateRange}
                                activityType={activityType}
                                onChange={(value) => form.setFieldValue('dateRange', value)}
                            />
                        </div>
                    )}
                    {activityType === PlaceType.Transit && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <span className={stepWizardClasses.progress}>
                                Destination:{' '}
                                <span className={stepWizardClasses.fieldValue}>{endLocation ?? '—'}</span>
                            </span>
                            <TextInput
                                value={endQuery}
                                placeholder="Search for a destination"
                                onChange={e => setEndQuery(e.currentTarget.value)}
                                onKeyUp={blurOnEnterKey}
                                rightSection={
                                    <ClearTextAreaRightSection
                                        onClear={() => {
                                            setEndQuery('');
                                            setEndLocation(undefined);
                                            setEndAddress(undefined);
                                            setEndPosition(undefined);
                                        }}
                                    />
                                }
                            />
                            <SearchChipsAndList
                                searchQuery={newSearchQuery(
                                    {
                                        searchTerm: endQuery,
                                        chipConfig: {
                                            tripGuideChips: true,
                                            recents: true,
                                            savedPlaces: true,
                                            searchEngine: true,
                                        },
                                        emptyQueryBehavior: 'match-none',
                                        bias: computeBias([
                                            positionFromLatLng(trip),
                                            positionFromLatLng(currentTrip),
                                        ]),
                                    },
                                    () => {}
                                )}
                                places={[]}
                                userTrips={[]}
                                currentUserId={currentUserId ?? undefined}
                                uiState="inline"
                                detailMode="results"
                                notifySearchHits={() => {}}
                                onClick={async (result) => {
                                    if (result.type === 'place') {
                                        setEndLocation(result.place.location ?? undefined);
                                        setEndAddress(result.place.address ?? undefined);
                                        setEndPosition(positionFromLatLng(result.place));
                                    } else {
                                        setEndLocation(result.title ?? undefined);
                                        setEndPosition(await result.position());
                                        setEndAddress((await result.full_address()) ?? undefined);
                                    }
                                    setEndQuery(''); // clear the search box + results once picked
                                }}
                            />
                        </div>
                    )}
                    {place.placeclass === PlaceClassType.Activity && (
                        <Switch
                            label="Draft"
                            checked={draft}
                            onChange={(e) => setDraft(e.currentTarget.checked)}
                            size="sm"
                        />
                    )}
                    {place.placeclass === PlaceClassType.Saved && (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span className={stepWizardClasses.progress}>
                                Saved Place Category:
                            </span>
                            <div style={{ display: 'flex', flexWrap: 'nowrap', overflowY: 'auto' }}>
                                <Chip.Group multiple>
                                    <Chip
                                        value="Favorite"
                                        size="xs"
                                        checked={categories.includes('Favorite')}
                                        onClick={(event) =>
                                            manageCategories(
                                                event.currentTarget.checked,
                                                'Favorite'
                                            )
                                        }
                                    >
                                        Favorite
                                    </Chip>
                                    <Chip
                                        value="Want to go"
                                        size="xs"
                                        checked={categories.includes('Want to go')}
                                        onClick={(event) =>
                                            manageCategories(
                                                event.currentTarget.checked,
                                                'Want to go'
                                            )
                                        }
                                    >
                                        Want to go
                                    </Chip>
                                </Chip.Group>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {currentStep === 2 && (
                <div className={`${stepWizardClasses.stepSection} keyboard-height-padded fade-in`}>
                    <hr style={{ width: '100%' }} />
                    <TSScrollable keyboardHeightAware>
                        <div className={stepWizardClasses.progress}>
                            Title:{' '}
                            <span className={stepWizardClasses.fieldValue}>
                                {form.values.title ?? ''}
                            </span>
                        </div>
                        <div className={stepWizardClasses.progress}>
                            Location:{' '}
                            <span className={stepWizardClasses.fieldValue}>
                                {form.values.location}
                            </span>
                        </div>
                        <div className={stepWizardClasses.progress}>
                            Type:{' '}
                            <span className={stepWizardClasses.fieldValue}>{placeTypeLabel(activityType)}</span>
                        </div>
                        <Textarea
                            label={
                                <Text ref={addressTitleTextAreaRef}>
                                    {activityType === PlaceType.Transit ? 'Origin address' : 'Address'}
                                </Text>
                            }
                            placeholder="Address"
                            {...form.getInputProps('address')}
                            onFocus={() => addressTitleTextAreaRef.current?.scrollIntoView()}
                            onChange={(event) => {
                                // onChange is fired on blur, not incrementally. So we should just call this ~once per edit.
                                const newValue = event.currentTarget.value;
                                form.setFieldValue('address', newValue);
                                setLocality(undefined);
                                setPosition(undefined);
                            }}
                        />
                        {activityType === PlaceType.Transit && (endPosition || endAddress || endLocation) && (
                            <Textarea
                                label={<Text>Destination address</Text>}
                                placeholder="Destination address"
                                value={endAddress ?? ''}
                                onChange={event => setEndAddress(event.currentTarget.value)}
                            />
                        )}
                        {!place.docid && place.placeclass === PlaceClassType.Activity && (
                            <>
                                <Textarea
                                    label={<Text ref={commentTitleTextAreaRef}>Comment</Text>}
                                    placeholder="Enter a comment..."
                                    {...form.getInputProps('comment')}
                                    onFocus={() =>
                                        commentTitleTextAreaRef.current?.scrollIntoView()
                                    }
                                    onChange={(event) => {
                                        // onChange is fired on blur, not incrementally. So we should just call this ~once per edit.
                                        const newValue = event.currentTarget.value;
                                        form.setFieldValue('comment', newValue);
                                    }}
                                />
                                <Space h={330} />
                            </>
                        )}
                    </TSScrollable>
                </div>
            )}
        </StepWizard>
    );
}
